// Eskom PARCC — the one dropdown, in two shapes.
//
// PARITY BY CONSTRUCTION. There is a single component: pages call it once and it
// renders a native <select> under Legacy (so Legacy stays pixel-identical to what
// it has always been) and the handoff's designed listbox under Lumen. Both carry
// the SAME accessible name, the SAME options and the SAME onChange contract, so
// a control can never exist in one theme and not the other — which is what the
// theme-parity test asserts.
//
// The Lumen menu is positioned `fixed` at the trigger's rect so it escapes the
// `overflow:auto` clipping of filter bars, table wrappers and flyouts (the reason
// the prototype uses one shared, portalled menu rather than an inline popover).
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search as SearchIcon } from 'lucide-react';
import { useVariant } from '../../lib/uiPrefs';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/** A labelled cluster of options — renders as <optgroup> in Legacy and as a
 *  non-selectable heading row in the Lumen menu. */
export interface SelectOptionGroup {
  group: string;
  options: readonly SelectOption[];
}

export type SelectItem = SelectOption | SelectOptionGroup;

function isGroup(item: SelectItem): item is SelectOptionGroup {
  return (item as SelectOptionGroup).group !== undefined;
}

/** Flatten items to the selectable options, in menu order. */
function flatten(items: readonly SelectItem[]): SelectOption[] {
  const out: SelectOption[] = [];
  for (const item of items) {
    if (isGroup(item)) out.push(...item.options);
    else out.push(item);
  }
  return out;
}

/** Keep only items whose label matches `query` (case-insensitive substring).
 *  A group survives if any of its options match, carrying just those options. */
function filterItems(items: readonly SelectItem[], query: string): SelectItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items as SelectItem[];
  const out: SelectItem[] = [];
  for (const item of items) {
    if (isGroup(item)) {
      const kept = item.options.filter((o) => o.label.toLowerCase().includes(q));
      if (kept.length) out.push({ group: item.group, options: kept });
    } else if (item.label.toLowerCase().includes(q)) {
      out.push(item);
    }
  }
  return out;
}

export interface SelectProps {
  /** The current value. An unmatched value renders `placeholder`. */
  value: string;
  options: readonly SelectItem[];
  onChange: (value: string) => void;
  /** Accessible name. REQUIRED — a <label> cannot name a <button>, so the Lumen
   *  trigger needs it explicitly for the two themes to expose the same name. */
  ariaLabel: string;
  disabled?: boolean;
  /** Shown when the value matches no option (e.g. the "View as…" prompt). */
  placeholder?: string;
  title?: string;
  className?: string;
  /** Extra classes for the Lumen trigger only (Legacy ignores it). */
  triggerClassName?: string;
  /** Inline styles applied to the control in BOTH shapes (sizing constraints). */
  style?: React.CSSProperties;
  /** Form field name. Set on the native <select> under Legacy and mirrored as
   *  `data-name` on the Lumen trigger, so a selector finds the control in either
   *  theme. */
  name?: string;
  /** Render the DESIGNED listbox even under Legacy (instead of a native
   *  <select>). For controls that want the richer dropdown — a live search box,
   *  grouped results — regardless of theme (e.g. the admin "View as" picker). */
  alwaysDesigned?: boolean;
  /** Add a search box at the top of the designed menu that filters the options
   *  live (case-insensitive on label). Long lists stay usable without scrolling.
   *  Ignored by the native <select>. */
  searchable?: boolean;
}

export function Select({
  value,
  options,
  onChange,
  ariaLabel,
  disabled = false,
  placeholder,
  title,
  className,
  triggerClassName,
  style,
  name,
  alwaysDesigned = false,
  searchable = false,
}: SelectProps): React.JSX.Element {
  const variant = useVariant();
  if (variant === 'lumen' || alwaysDesigned) {
    return (
      <LumenSelect
        value={value}
        options={options}
        onChange={onChange}
        ariaLabel={ariaLabel}
        disabled={disabled}
        placeholder={placeholder}
        title={title}
        className={className}
        triggerClassName={triggerClassName}
        style={style}
        name={name}
        searchable={searchable}
      />
    );
  }
  return (
    <select
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      aria-label={ariaLabel}
      title={title}
      className={className}
      style={style}
    >
      {placeholder !== undefined && flatten(options).every((o) => o.value !== value) && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {options.map((item) =>
        isGroup(item) ? (
          <optgroup key={item.group} label={item.group}>
            {item.options.map((o) => (
              <option key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
              </option>
            ))}
          </optgroup>
        ) : (
          <option key={item.value} value={item.value} disabled={item.disabled}>
            {item.label}
          </option>
        ),
      )}
    </select>
  );
}

/** Menu geometry, recomputed each time the menu opens. */
interface Rect {
  left: number;
  top: number;
  width: number;
}

const MENU_MAX_HEIGHT = 256;

function LumenSelect({
  value,
  options,
  onChange,
  ariaLabel,
  disabled,
  placeholder,
  title,
  className,
  triggerClassName,
  style,
  name,
  searchable = false,
}: SelectProps): React.JSX.Element {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<Rect | null>(null);
  const [query, setQuery] = useState('');
  const listId = useId();

  // The label on the trigger always reflects the FULL option set (the current
  // value), independent of any active search filter.
  const selected = flatten(options).find((o) => o.value === value);
  const label = selected?.label ?? placeholder ?? '';

  // What the menu shows: filtered by the live query when searchable.
  const visibleItems = useMemo(
    () => (searchable ? filterItems(options, query) : (options as SelectItem[])),
    [options, query, searchable],
  );
  const flat = useMemo(() => flatten(visibleItems), [visibleItems]);

  // Keyboard focus within the menu. Seeded to the selected row on open.
  const [activeIndex, setActiveIndex] = useState(0);

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) return;
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // A searchable menu always opens on a clean, unfiltered list.
    setQuery('');
    // Flip above the trigger when there is not enough room below. A searchable
    // menu reserves a row for its search box.
    const below = window.innerHeight - r.bottom;
    const height = Math.min(MENU_MAX_HEIGHT, flat.length * 34 + (searchable ? 52 : 10));
    const top = below < height + 12 && r.top > height + 12 ? r.top - height - 6 : r.bottom + 6;
    setRect({ left: r.left, top, width: r.width });
    const current = flat.findIndex((o) => o.value === value);
    setActiveIndex(current >= 0 ? current : 0);
    setOpen(true);
  }, [disabled, flat, value, searchable]);

  // On open, focus the search box (searchable) or the menu container, so arrow
  // keys and Esc are handled from the first keystroke.
  useLayoutEffect(() => {
    if (!open) return;
    if (searchable) searchRef.current?.focus();
    else menuRef.current?.focus();
  }, [open, searchable]);

  // Keep the active row valid as the filter narrows the list.
  useEffect(() => {
    setActiveIndex((i) => (i >= flat.length ? 0 : i));
  }, [flat.length]);

  // Close on anything that would move the trigger out from under the menu. The
  // menu is `fixed`, so a scroll elsewhere on the page would otherwise leave it
  // stranded. `capture` catches scrolls inside overflow containers too.
  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = (): void => setOpen(false);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  function pick(option: SelectOption): void {
    if (option.disabled) return;
    onChange(option.value);
    close();
  }

  /** Step the active row, skipping disabled options. */
  function step(from: number, delta: number): number {
    let i = from;
    for (let guard = 0; guard < flat.length; guard += 1) {
      i = (i + delta + flat.length) % flat.length;
      if (!flat[i]?.disabled) return i;
    }
    return from;
  }

  function onMenuKeyDown(e: React.KeyboardEvent): void {
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        close();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => step(i, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => step(i, -1));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(step(flat.length - 1, 1));
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(step(0, -1));
        break;
      case 'Enter': {
        e.preventDefault();
        const option = flat[activeIndex];
        if (option) pick(option);
        break;
      }
      case ' ': {
        // Space picks only when navigating the list; in the search box it must
        // type a space, so leave it alone there.
        if (searchable) break;
        e.preventDefault();
        const option = flat[activeIndex];
        if (option) pick(option);
        break;
      }
      case 'Tab':
        setOpen(false);
        break;
      default:
        break;
    }
  }

  function onTriggerKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu();
    }
  }

  let flatIndex = -1;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={['lm-select-trigger', triggerClassName, className].filter(Boolean).join(' ')}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onTriggerKeyDown}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        title={title}
        style={style}
        data-name={name}
      >
        <span>{label}</span>
        <ChevronDown size={14} aria-hidden style={{ flex: 'none', color: 'var(--muted)' }} />
      </button>

      {open &&
        rect &&
        createPortal(
          <>
            {/* Outside-click catcher, below the menu in the stack. */}
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 240 }}
              onClick={() => setOpen(false)}
              aria-hidden
            />
            <div
              ref={menuRef}
              id={listId}
              role="listbox"
              aria-label={ariaLabel}
              aria-activedescendant={`${listId}-opt-${activeIndex}`}
              tabIndex={-1}
              className="lm-select-menu"
              onKeyDown={searchable ? undefined : onMenuKeyDown}
              style={{
                left: rect.left,
                top: rect.top,
                minWidth: Math.max(rect.width, 180),
                maxHeight: MENU_MAX_HEIGHT,
              }}
            >
              {searchable && (
                <div className="lm-select-search">
                  <SearchIcon size={14} aria-hidden style={{ color: 'var(--faint)', flex: 'none' }} />
                  <input
                    ref={searchRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onMenuKeyDown}
                    placeholder="Search…"
                    aria-label={`Search ${ariaLabel}`}
                    aria-controls={listId}
                  />
                </div>
              )}
              <div className="lm-select-scroll">
                {visibleItems.map((item) =>
                  isGroup(item) ? (
                    <div key={`g-${item.group}`}>
                      <div className="lm-select-option is-group" role="presentation">
                        {item.group}
                      </div>
                      {item.options.map((o) => {
                        flatIndex += 1;
                        return renderOption(o, flatIndex);
                      })}
                    </div>
                  ) : (
                    (() => {
                      flatIndex += 1;
                      return renderOption(item, flatIndex);
                    })()
                  ),
                )}
                {flat.length === 0 && <div className="lm-select-empty">No matches.</div>}
              </div>
            </div>
          </>,
          document.body,
        )}
    </>
  );

  function renderOption(option: SelectOption, index: number): React.JSX.Element {
    const isSelected = option.value === value;
    return (
      <button
        key={option.value}
        id={`${listId}-opt-${index}`}
        type="button"
        role="option"
        aria-selected={isSelected}
        disabled={option.disabled}
        className={`lm-select-option${index === activeIndex ? ' is-active' : ''}`}
        onMouseEnter={() => setActiveIndex(index)}
        onClick={() => pick(option)}
      >
        <span>{option.label}</span>
        {isSelected && (
          <Check size={15} aria-hidden style={{ color: 'var(--accent)', flex: 'none' }} />
        )}
      </button>
    );
  }
}
